import clonedeep from "lodash.clonedeep";
import { ComputedRef } from "vue";
import {
  SportEvent,
  Option,
  Links,
  Offer,
  Market
} from "@fresh8/types/src/betData";
import { BetSelection } from "@fresh8/types/src/betSelection";
import { Macro } from "@fresh8/types/src";

const supportedUtmMacros = [
  "utm_source",
  "utm_content",
  "utm_campaign",
  "utm_medium",
  "utm_term",
  "utm_id",
  "utm_item"
];

function addKeyValue(link: string, key: string, value: string | number) {
  if (!link) return "";

  if (
    key === "targetUrl" &&
    typeof value === "string" &&
    value.charAt(value.length - 1) === "?"
  ) {
    value = value.slice(0, value.length - 1);
  }

  if (link.includes("?")) {
    return `${link}&${key}=${encodeURIComponent(value)}`;
  }

  return `${link}?${key}=${encodeURIComponent(value)}`;
}

const searchParams = new URLSearchParams(window.location.search);

function getLinksUrl(): string {
  return process.env.NODE_ENV !== "production"
    ? "https://links-staging.fresh8.co/"
    : "https://links.fresh8.co/";
}

export function links({
  clickUrl,
  targetUrl,
  affiliateId,
  operatorKey
}: {
  operatorKey: string;
  clickUrl?: string;
  targetUrl?: string;
  affiliateId?: string;
}): Links {
  const trackingID = searchParams.get("trackingID") || undefined;
  const links = {
    operator: "",
    registration: ""
  };

  return Object.keys(links).reduce((updatedLinks, link) => {
    updatedLinks[link] = getLinksUrl();
    updatedLinks[link] = addKeyValue(
      updatedLinks[link],
      "operator",
      operatorKey
    );

    updatedLinks[link] = addKeyValue(
      updatedLinks[link],
      "type",
      link === "operator" ? "operator" : "reg"
    );

    if (clickUrl) {
      updatedLinks[link] = addKeyValue(
        updatedLinks[link],
        "clickUrl",
        clickUrl
      );
    }

    if (targetUrl) {
      updatedLinks[link] = addKeyValue(
        updatedLinks[link],
        "targetUrl",
        targetUrl
      );
    }

    if (affiliateId) {
      updatedLinks[link] = addKeyValue(
        updatedLinks[link],
        "affiliateId",
        affiliateId
      );
    }

    if (trackingID) {
      updatedLinks[link] = addKeyValue(
        updatedLinks[link],
        "externalId",
        trackingID
      );
    }

    return updatedLinks;
  }, links);
}

export function option(
  option: Option,
  market: Market,
  {
    clickUrl,
    targetUrl,
    affiliateId,
    operatorKey,
    stake
  }: {
    clickUrl?: string;
    targetUrl?: string;
    affiliateId?: string;
    operatorKey: string;
    stake: number;
  }
): Option {
  const _option = clonedeep(option);
  const trackingID = searchParams.get("trackingID") || undefined;

  _option.betslipUrl = getLinksUrl();
  _option.betslipUrl = addKeyValue(_option.betslipUrl, "operator", operatorKey);
  _option.betslipUrl = addKeyValue(_option.betslipUrl, "optionId", _option.id);
  _option.betslipUrl = addKeyValue(
    _option.betslipUrl,
    "eventId",
    market.eventId
  );

  if (stake) {
    _option.betslipUrl = addKeyValue(_option.betslipUrl, "stake", stake);
  }

  if (clickUrl) {
    _option.betslipUrl = addKeyValue(_option.betslipUrl, "clickUrl", clickUrl);
  }

  if (targetUrl) {
    _option.betslipUrl = addKeyValue(
      _option.betslipUrl,
      "targetUrl",
      targetUrl
    );
  }

  if (affiliateId) {
    _option.betslipUrl = addKeyValue(
      _option.betslipUrl,
      "affiliateId",
      affiliateId
    );
  }

  if (trackingID) {
    _option.betslipUrl = addKeyValue(
      _option.betslipUrl,
      "externalId",
      trackingID
    );
  }

  return _option;
}

export function options(
  options: Record<string, Option>,
  markets: Record<string, Market>,
  {
    clickUrl,
    targetUrl,
    affiliateId,
    operatorKey,
    stake
  }: {
    clickUrl?: string;
    targetUrl?: string;
    affiliateId?: string;
    operatorKey: string;
    stake: number;
  }
): Record<string, Option> {
  return Object.keys(options).reduce((updatedOptions, optionId) => {
    if (typeof updatedOptions[optionId] !== "string") {
      const _option = updatedOptions[optionId];

      updatedOptions[optionId] = option(
        updatedOptions[optionId],
        markets[_option.marketID],
        {
          clickUrl,
          targetUrl,
          affiliateId,
          operatorKey,
          stake
        }
      );
    }

    return updatedOptions;
  }, clonedeep(options) as Record<string, Option>);
}

export function event(
  event: SportEvent,
  {
    clickUrl,
    targetUrl,
    affiliateId,
    operatorKey
  }: {
    clickUrl?: string;
    targetUrl?: string;
    affiliateId?: string;
    operatorKey: string;
  }
): SportEvent {
  const trackingID = searchParams.get("trackingID") || undefined;
  const _event = clonedeep(event);

  _event.eventUrl = getLinksUrl();
  _event.eventUrl = addKeyValue(_event.eventUrl, "operator", operatorKey);
  _event.eventUrl = addKeyValue(_event.eventUrl, "eventId", _event.id);

  if (clickUrl) {
    _event.eventUrl = addKeyValue(_event.eventUrl, "clickUrl", clickUrl);
  }

  if (targetUrl) {
    _event.eventUrl = addKeyValue(_event.eventUrl, "targetUrl", targetUrl);
  }

  if (affiliateId) {
    _event.eventUrl = addKeyValue(_event.eventUrl, "affiliateId", affiliateId);
  }

  if (trackingID) {
    _event.eventUrl = addKeyValue(_event.eventUrl, "externalId", trackingID);
  }

  return _event;
}

export function events(
  events: Record<string, SportEvent>,
  {
    clickUrl,
    targetUrl,
    affiliateId,
    operatorKey
  }: {
    clickUrl?: string;
    targetUrl?: string;
    affiliateId?: string;
    operatorKey: string;
  }
): Record<string, SportEvent> {
  return (
    events &&
    Object.keys(events).reduce((updatedOptions, eventId) => {
      if (typeof updatedOptions[eventId] !== "string") {
        updatedOptions[eventId] = event(updatedOptions[eventId], {
          clickUrl,
          targetUrl,
          affiliateId,
          operatorKey
        });
      }

      return updatedOptions;
    }, clonedeep(events))
  );
}

export function offerLink(
  link: string,
  {
    affiliateId,
    clickUrl,
    operatorKey
  }: {
    affiliateId?: string;
    clickUrl?: string;
    operatorKey: string;
  }
): string {
  let _link = link;

  const trackingID = searchParams.get("trackingID") || undefined;

  _link = getLinksUrl();
  _link = addKeyValue(_link, "operator", operatorKey);

  if (clickUrl) {
    _link = addKeyValue(_link, "clickUrl", clickUrl);
  }

  if (link) {
    _link = addKeyValue(_link, "targetUrl", link);
  }

  if (affiliateId) {
    _link = addKeyValue(_link, "affiliateId", affiliateId);
  }

  if (trackingID) {
    _link = addKeyValue(_link, "externalId", trackingID);
  }

  return _link;
}

export function offer(
  offer: Offer,
  {
    affiliateId,
    clickUrl,
    operatorKey
  }: { affiliateId?: string; clickUrl?: string; operatorKey: string }
): Offer {
  const _offer: Offer = clonedeep(offer);

  _offer.link = offerLink(_offer.link, {
    affiliateId,
    clickUrl,
    operatorKey
  });

  return _offer;
}

export function generate(
  targetLink: string,
  {
    affiliateId,
    clickUrl,
    operatorKey
  }: { affiliateId?: string; clickUrl?: string; operatorKey: string }
): string {
  let link = getLinksUrl();
  link = addKeyValue(link, "operator", operatorKey);

  const trackingID = searchParams.get("trackingID") || undefined;

  if (targetLink) {
    link = addKeyValue(link, "targetUrl", targetLink);
  }

  if (clickUrl) {
    link = addKeyValue(link, "clickUrl", clickUrl);
  }

  if (affiliateId) {
    link = addKeyValue(link, "affiliateId", affiliateId);
  }

  if (trackingID) {
    link = addKeyValue(link, "externalId", trackingID);
  }

  return link;
}

export function acca({
  operatorKey,
  stake,
  affiliateId,
  targetUrl,
  getSelections,
  options,
  clickUrl
}: {
  operatorKey: string;
  stake: number;
  affiliateId?: string;
  targetUrl?: string;
  getSelections: ComputedRef<BetSelection[]>;
  options?: Record<string, Option> | undefined;
  clickUrl?: string;
}): string {
  let generatedLink = `${getLinksUrl()}redirect/acca`;

  generatedLink = addKeyValue(generatedLink, "operator", operatorKey);
  generatedLink = addKeyValue(generatedLink, "stake", stake);

  if (affiliateId) {
    generatedLink = addKeyValue(generatedLink, "affiliateId", affiliateId);
  }

  if (targetUrl) {
    generatedLink = addKeyValue(generatedLink, "targetURL", targetUrl);
  }

  if (getSelections.value.length !== 0) {
    let selectionValuesText = "";

    getSelections.value.map(selection => {
      if (selection.optionId in (options || {})) {
        const option = options?.[selection.optionId]?.id;
        const market = options?.[selection.optionId]?.marketID;

        selectionValuesText = `${selectionValuesText}&selections=${option}|${market}|${selection.eventId}`;
      }
    });

    generatedLink = `${generatedLink}${selectionValuesText}`;
  }

  if (clickUrl) {
    generatedLink = addKeyValue(generatedLink, "clickUrl", clickUrl);
  }
  return generatedLink;
}

export function getClickUrl(
  adtagClickUrl: string | undefined,
  adtagClickUrlBehaviour: string | undefined,
  clickUrlMacro: string
): string | undefined {
  // reset adtagClickUrl so it doesn't get applied to any
  // of the generated links and only fired in background.
  if (adtagClickUrlBehaviour === "background") {
    adtagClickUrl = "";
  }
  if (adtagClickUrl !== "" && clickUrlMacro !== "") {
    return adtagClickUrl + clickUrlMacro;
  }
  return adtagClickUrl || clickUrlMacro || undefined;
}

export function getClickThroughUrl(
  adtagClickUrl: string | undefined,
  adtagClickUrlBehaviour: string | undefined
): string | undefined {
  if (adtagClickUrlBehaviour === "background") {
    return adtagClickUrl;
  }

  return "";
}

const Ceasersteam = "5ef071fca75728001bc395ad";
const CBSteam = "5e623d617275e900193922c7";

// getAffiliateId combines all affiliate Ids from all all sources, including Adtag, macros and adtag macros.
export function getAffiliateId(
  adtagAffiliateId: string,
  macroAffiliateId: string,
  teamId: string,
  macros: Macro[] | null
): string | undefined {
  const searchParams = new URLSearchParams(window.location.search);

  if (teamId == CBSteam || teamId == Ceasersteam) {
    return cbsCustomTracking(adtagAffiliateId);
  }

  // if both adtag and macro affilaite ids are found then combine them
  if (adtagAffiliateId !== "" && macroAffiliateId !== "") {
    const adtagSearchParams = new URLSearchParams(adtagAffiliateId);
    const macroSearchParams = new URLSearchParams(macroAffiliateId);
    for (const [key, val] of Array.from(macroSearchParams.entries())) {
      if (!adtagSearchParams.has(key)) {
        adtagSearchParams.append(key, val);
      } else {
        adtagSearchParams.set(key, val);
      }
    }
    if (macros && macros.length > 0) {
      return addMacroTracking(
        adtagSearchParams.toString(),
        macros,
        searchParams
      );
    }
    return adtagSearchParams.toString();
  }

  // add adtag macros to any affilaite ids found
  if (macros && macros.length > 0) {
    return addMacroTracking(
      adtagAffiliateId || macroAffiliateId || "",
      macros,
      searchParams
    );
  }

  return adtagAffiliateId || macroAffiliateId || undefined;
}

// addMacroTracking adds any macros which are selected to add to targetUrls on the adtag.
function addMacroTracking(
  affiliateId: string | undefined,
  macros: Macro[],
  searchParams: URLSearchParams
): string | undefined {
  const adtagSearchParams = new URLSearchParams(affiliateId);
  for (const macro of macros) {
    for (const query of macro.query) {
      if (!searchParams.get(query.key)) {
        continue;
      }
      const val = searchParams.get(query.key);
      if (!val || val.includes("%")) {
        continue;
      }
      if (!query.track && !supportedUtmMacros.includes(query.key)) {
        continue;
      }
      adtagSearchParams.append(query.key, val);
    }
  }

  return adtagSearchParams.toString();
}

// cbsCustomTracking adds the value of customField1 to their affiliate ids.
function cbsCustomTracking(affiliateId: string): string {
  const searchParams = new URLSearchParams(window.location.search);

  const customField1 = searchParams.get("customField1");
  if (!customField1) {
    return affiliateId;
  }
  const adtagSearchParams = new URLSearchParams(affiliateId);
  adtagSearchParams.set("c", customField1);
  adtagSearchParams.set("utm_campaign", customField1);
  adtagSearchParams.set("ar", customField1);

  return adtagSearchParams.toString();
}

/**
 * getTargetUrl proritises what URL should be used when a custom URL has been defined.
 * Current order:
 * - Macro (iframe.fresh8.co?targetUrl=http://fresh8gaming.com)
 * - Campaign
 * - Ad Tag Fallback (if useDeeplinking is false and the fallback url isn't empty)
 * - Ad Tag custom url (being depracated)
 */
export function getTargetUrl(
  // The target URL defined on the ad tag
  adtagTargetUrl: string | "",
  // The target URL defined on the campaign
  campaignTargetUrl: string,
  // The target URL defined as a macro on the ad tag as query string
  macroTargetUrl: string,
  // The ad tag fallback url that that should be used if deeplinking is disabled
  fallbackUrl: string,
  // Does the ad tag have deeplinking enabled
  useDeeplinking: boolean
): string {
  if (macroTargetUrl !== "") {
    return macroTargetUrl;
  }

  if (campaignTargetUrl && campaignTargetUrl !== "") {
    return campaignTargetUrl;
  }

  if (!useDeeplinking && fallbackUrl !== "") {
    return fallbackUrl;
  }

  return adtagTargetUrl;
}
